import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

// Components
import {
	Breadcrumb,
	Layout,
	PageHeader,
	Input,
	Button,
	Upload,
	message,
} from 'antd'
import {
	SendOutlined,
	PaperClipOutlined,
	CloseOutlined,
	DownloadOutlined,
} from '@ant-design/icons'
import MainLayout from 'components/mainLayout'
import WithDemandDetails from 'components/withDemandDetails'

// Utils
import { formatDate } from 'utils/formatters'

// Services
import {
	listMessages,
	createMessage,
	downloadMessageAttachment,
} from 'services/demands/demandsServices'
import { profileNameMap } from 'utils/constants'

// Styles
import styles from './index.module.scss'

const triggerFileDownload = (blob, filename) => {
	const url = window.URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.style.display = 'none'
	a.href = url
	a.download = filename
	document.body.appendChild(a)
	a.click()
	window.URL.revokeObjectURL(url)
	document.body.removeChild(a)
}

function DemandChat({ id, history, data }) {
	const [messages, setMessages] = useState([])
	const [loading, setLoading] = useState(true)
	const [sending, setSending] = useState(false)
	const [skip, setSkip] = useState(0)
	const [currentMessage, setCurrentMessage] = useState('')
	const [fileList, setFileList] = useState([])
	const sessionState = useSelector(({ session }) => session)
	const currentUserId = sessionState?.id
	const currentUserName = sessionState?.name
	const currentUserRole = sessionState?.perfil
		? profileNameMap[sessionState.perfil]
		: 'Solicitante'

	useEffect(() => {
		let isMounted = true

		const fetchMessages = async () => {
			if (!id) return

			if (isMounted) setLoading(true)

			try {
				const response = await listMessages(id, skip)
				if (isMounted) {
					setMessages(response.data.messages)
				}
			} catch (error) {
				console.error('Erro ao buscar mensagens:', error)
				if (isMounted) {
					message.error('Não foi possível carregar as mensagens.')
				}
			} finally {
				if (isMounted) {
					setLoading(false)
				}
			}
		}

		fetchMessages()

		return () => {
			isMounted = false
		}
	}, [id, skip])

	const handleSendMessage = async () => {
		if (!currentMessage.trim() && fileList.length === 0) return
		const tempId = `temp_${Date.now()}`

		const tempMessage = {
			id: tempId,
			user_id: currentUserId,
			User: {
				name: currentUserName,
				role: currentUserRole,
			},
			text: currentMessage,
			TasksAttachments: fileList.map((file) => ({
				id: `temp_file_${file.uid}`,
				original_name: file.name,
				file_size: `${(file.size / 1024).toFixed(1)} KB`,
			})),
			created_at: new Date().toISOString(),
		}

		setSending(true)

		const feedbackKey = `send_${tempId}`
		message.loading({
			content: 'Enviando mensagem...',
			key: feedbackKey,
			duration: 0,
		})

		const formData = new FormData()
		formData.append('text', currentMessage)
		if (fileList.length > 0) {
			formData.append('attachment', fileList[0])
		}

		setMessages((prevMessages) => [...prevMessages, tempMessage])
		setCurrentMessage('')
		setFileList([])

		try {
			const data = await createMessage(id, formData)
			if (!data) throw new Error('Erro ao enviar mensagem')
			setMessages((prev) =>
				prev.map((msg) => {
					if (msg.id === tempId) {
						const serverMessage = data
						return {
							...msg,
							...serverMessage,
							user_id: serverMessage.user_id || msg.user_id,
							User: serverMessage.User || msg.User,
						}
					}
					return msg
				})
			)
			message.success({ content: 'Mensagem enviada!', key: feedbackKey })
		} catch (error) {
			let finalErrorMessage = 'Erro! Falha ao enviar mensagem.'
			if (error.response && error.response.status === 415) {
				finalErrorMessage = 'Erro! Formato de arquivo inválido'
			} else if (error.response && error.response.status === 413) {
				finalErrorMessage =
					'Erro! O arquivo é muito grande para ser enviado. Limite permitido: 30mb'
			} else if (error.response && error.response.data) {
				finalErrorMessage = `Erro! ${error.response.data}`
			}
			message.error({ content: finalErrorMessage, key: feedbackKey })
			setMessages((prevMessages) => prevMessages.filter((m) => m.id !== tempId))
			console.error('Erro ao enviar mensagem: ', error)
		} finally {
			setSending(false)
		}
	}

	const handleDownload = async (messageId, attachmentData) => {
		const filename = attachmentData?.original_name || 'download'
		message.loading({ content: 'Baixando arquivo...', key: 'download' })
		try {
			const data = await downloadMessageAttachment(id, messageId)
			triggerFileDownload(data, filename)
			message.success({ content: 'Download concluído!', key: 'download' })
		} catch (error) {
			console.error('Erro ao baixar arquivo:', error)
		}
	}

	const handleKeyDown = (e) => {
		// Check if Enter is pressed without Shift
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			handleSendMessage()
		}
	}

	const uploadProps = {
		onRemove: (file) => {
			setFileList([])
		},
		beforeUpload: (file) => {
			setFileList([file])
			return false
		},
		fileList,
		showUploadList: false,
		multiple: false,
	}

	useEffect(() => {
		const timer = setTimeout(() => {
			try {
				if (
					window.$chatwoot &&
					typeof window.$chatwoot.toggleBubbleVisibility === 'function'
				) {
					window.$chatwoot.toggleBubbleVisibility('hide')
				}
			} catch (_) {}
		}, 100)

		return () => {
			clearTimeout(timer)
			try {
				if (
					window.$chatwoot &&
					typeof window.$chatwoot.toggleBubbleVisibility === 'function'
				) {
					window.$chatwoot.toggleBubbleVisibility('show')
				}
			} catch (_) {}
		}
	}, [])

	return (
		<MainLayout>
			<Layout style={{ backgroundColor: '#fff' }}>
				<PageHeader>
					<Breadcrumb>
						<Breadcrumb.Item
							onClick={() => history.push('/demands')}
							style={{ cursor: 'pointer' }}>
							Demandas
						</Breadcrumb.Item>
						<Breadcrumb.Item
							onClick={() => history.push(`/demands/${id}`)}
							style={{ cursor: 'pointer' }}>
							{data.name}
						</Breadcrumb.Item>
						<Breadcrumb.Item>Chat</Breadcrumb.Item>
					</Breadcrumb>
				</PageHeader>
				<Layout style={{ backgroundColor: '#fff', flex: 1 }}>
					<Layout.Content className={styles.chatContent}>
						{messages.map((msg) => {
							const isSelf = msg.user_id === currentUserId
							return (
								<div
									key={msg.id}
									className={`${styles.messageBubble} ${isSelf ? styles.self : styles.other}`}>
									<div className={styles.messageTimestamp}>
										{formatDate(msg.created_at)}
									</div>
									<div className={styles.messageInfo}>
										<span className={styles.senderName}>{msg.User.name}</span>
										<span className={styles.senderRole}>
											{' '}
											-{' '}
											{msg.User.perfil
												? profileNameMap[msg.User.perfil]
												: 'Solicitante'}
										</span>
									</div>
									{msg.text && <p className={styles.messageText}>{msg.text}</p>}
									{msg.TasksAttachments && msg.TasksAttachments.length > 0 && (
										<div className={styles.messageFiles}>
											{msg.TasksAttachments.map((file) => (
												<div key={file.id} className={styles.fileItem}>
													<Button
														type="link"
														icon={<DownloadOutlined />}
														onClick={() => handleDownload(msg.id, file)}
														style={{ color: '#fff' }}>
														{file.original_name} ({file.file_size})
													</Button>
												</div>
											))}
										</div>
									)}
								</div>
							)
						})}
					</Layout.Content>
					<Layout.Footer className={styles.chatFooter}>
						{fileList.length > 0 && (
							<div className={styles.filePreviewContainer}>
								{fileList.map((file) => (
									<span
										key={file.uid}
										className={styles.filePreviewItem}
										style={{
											color: '#fff',
											backgroundColor: '#2d3748',
											padding: '4px 8px',
											marginBottom: '10px',
											borderRadius: '4px',
											display: 'inline-block',
										}}>
										{file.name}
										<Button
											type="text"
											size="small"
											danger
											icon={<CloseOutlined />}
											onClick={() => uploadProps.onRemove(file)}
										/>
									</span>
								))}
							</div>
						)}
						<Input.Group
							compact
							style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
							<Upload {...uploadProps}>
								<Button
									icon={<PaperClipOutlined style={{ fontSize: 16 }} />}
									disabled={fileList.length > 0}
									style={{
										width: 40,
										height: 40,
										borderRadius: '50%',
									}}
								/>
							</Upload>
							<Input.TextArea
								style={{ flex: 1, borderRadius: 6, paddingTop: 22 }}
								autoSize={{ minRows: 3, maxRows: 4 }}
								showCount
								maxLength={2000}
								placeholder="Sua mensagem"
								value={currentMessage}
								onChange={(e) => setCurrentMessage(e.target.value)}
								onKeyDown={handleKeyDown}
								disabled={sending}
							/>
							<Button
								style={{
									width: 40,
									height: 40,
									borderRadius: '50%',
								}}
								type="primary"
								icon={<SendOutlined />}
								onClick={handleSendMessage}
								loading={sending}
								disabled={
									(!currentMessage.trim() && fileList.length === 0) || sending
								}
							/>
						</Input.Group>
					</Layout.Footer>
				</Layout>
			</Layout>
		</MainLayout>
	)
}

export default WithDemandDetails(DemandChat)
